/**
 * SplitLoanPaymentModal Component
 * Modal for splitting a loan payment into principal (transfer) and loan expenses
 * WP-TRANSFER-1: Transfer Detection and Management
 *
 * Note: "Loan Expenses" includes all non-principal costs: interest, taxes,
 * insurance, escrow, PMI, etc. - not just interest.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Input, Alert } from '@/components/ui';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import { transferService, type SplitLoanPaymentResponse } from '@/services/transactions';
import type { TransactionWithSplits } from '@/services/transactions';
import type { Account } from '@/types';
import { ApiClientError } from '@/services/api';

// ============================================================================
// Types
// ============================================================================

export interface SplitLoanPaymentModalProps {
  transaction: TransactionWithSplits;
  loanAccounts: Account[];
  defaultLoanAccountId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: SplitLoanPaymentResponse) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function SplitLoanPaymentModal({
  transaction,
  loanAccounts,
  defaultLoanAccountId,
  isOpen,
  onClose,
  onSuccess,
}: SplitLoanPaymentModalProps) {
  // Total payment amount (should be negative for an expense)
  const totalAmount = Math.abs(transaction.amount);

  // State
  const [selectedLoanAccountId, setSelectedLoanAccountId] = useState<string>('');
  const [expenseAmountStr, setExpenseAmountStr] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Get selected loan account
  const selectedLoanAccount = useMemo(() => {
    return loanAccounts.find((acc) => acc.id === selectedLoanAccountId) ?? null;
  }, [loanAccounts, selectedLoanAccountId]);

  // Parse expense amount (interest, taxes, insurance, etc.)
  const expenseAmount = useMemo(() => {
    const parsed = parseFloat(expenseAmountStr);
    return isNaN(parsed) ? 0 : parsed;
  }, [expenseAmountStr]);

  // Calculate principal amount
  const principalAmount = useMemo(() => {
    return Math.max(0, totalAmount - expenseAmount);
  }, [totalAmount, expenseAmount]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Set default loan account
      const defaultId = defaultLoanAccountId ?? loanAccounts[0]?.id ?? '';
      setSelectedLoanAccountId(defaultId);
      setExpenseAmountStr('');
      setError(null);
      setValidationError(null);
    }
  }, [isOpen, defaultLoanAccountId, loanAccounts]);

  // Validate input
  useEffect(() => {
    if (!expenseAmountStr) {
      setValidationError(null);
      return;
    }

    const parsed = parseFloat(expenseAmountStr);
    if (isNaN(parsed)) {
      setValidationError('Please enter a valid number');
    } else if (parsed < 0) {
      setValidationError('Expense amount cannot be negative');
    } else if (parsed > totalAmount) {
      setValidationError('Expense amount cannot exceed total payment');
    } else if (parsed === totalAmount) {
      setValidationError('Principal amount must be greater than zero');
    } else {
      setValidationError(null);
    }
  }, [expenseAmountStr, totalAmount]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!selectedLoanAccount) {
      setError('Please select a loan account');
      return;
    }

    if (validationError || !expenseAmountStr) {
      setError('Please enter a valid expense amount');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await transferService.splitLoanPayment(transaction.id, {
        interestAmount: expenseAmount, // API expects interestAmount but we send total expenses
        loanAccountId: selectedLoanAccount.id,
      });
      onSuccess(response);
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to split loan payment');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validationError,
    expenseAmountStr,
    expenseAmount,
    selectedLoanAccount,
    transaction.id,
    onSuccess,
    onClose,
  ]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const displayName = transaction.merchantName || transaction.name || 'Unknown';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="split-loan-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2
            id="split-loan-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            Split Loan Payment
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-6">
          Split this loan payment into expenses (interest, taxes, insurance, etc.) and principal (transfer to loan account).
        </p>

        {/* Error message */}
        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Transaction info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Transaction</span>
            <span className="font-medium text-gray-900">{displayName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Total Payment</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(totalAmount, transaction.currency)}
            </span>
          </div>
        </div>

        {/* Loan account selection */}
        <div className="mb-6">
          <label htmlFor="loan-account-select" className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Loan Account
            </div>
          </label>
          <select
            id="loan-account-select"
            value={selectedLoanAccountId}
            onChange={(e) => setSelectedLoanAccountId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {loanAccounts.length === 0 ? (
              <option value="">No loan accounts available</option>
            ) : (
              loanAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                  {account.mask ? ` (***${account.mask})` : ''}
                </option>
              ))
            )}
          </select>
          {loanAccounts.length > 1 && (
            <p className="mt-1 text-xs text-gray-500">
              Select which loan this payment applies to
            </p>
          )}
        </div>

        {/* Loan expenses input */}
        <div className="mb-4">
          <Input
            label="Loan Expenses"
            type="number"
            step="0.01"
            min="0"
            max={totalAmount}
            value={expenseAmountStr}
            onChange={(e) => setExpenseAmountStr(e.target.value)}
            placeholder="0.00"
            error={validationError ?? undefined}
            hint="Interest, taxes, insurance, escrow, PMI, etc."
          />
        </div>

        {/* Calculated split */}
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">Loan Expenses</span>
            <span
              className={cn(
                'font-medium',
                expenseAmount > 0 ? 'text-red-600' : 'text-gray-400'
              )}
            >
              {expenseAmount > 0
                ? `-${formatCurrency(expenseAmount, transaction.currency)}`
                : formatCurrency(0, transaction.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between pb-3 border-b border-gray-200">
            <span className="text-sm text-gray-600">Principal (Transfer)</span>
            <span
              className={cn(
                'font-medium',
                principalAmount > 0 ? 'text-blue-600' : 'text-gray-400'
              )}
            >
              {formatCurrency(principalAmount, transaction.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between pt-3">
            <span className="text-sm font-medium text-gray-700">Total</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(totalAmount, transaction.currency)}
            </span>
          </div>
        </div>

        {/* Info note */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">
            <strong>Note:</strong> This will create two new transactions:
          </p>
          <ul className="mt-1 text-xs text-gray-500 list-disc list-inside">
            <li>A loan expense transaction (interest, taxes, insurance, etc.)</li>
            <li>A principal transfer to {selectedLoanAccount?.name ?? 'selected loan'}</li>
          </ul>
          <p className="mt-1 text-xs text-gray-500">
            The original transaction will be removed.
          </p>
        </div>

        {/* Footer buttons */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedLoanAccount || !expenseAmountStr || !!validationError || isSubmitting}
            isLoading={isSubmitting}
          >
            Split Payment
          </Button>
        </div>
      </div>
    </div>
  );
}
